<template>
  <ModalBase show-modal @close-modal="close" unpadded :loading="loadingModal" class="logo-generator-modal">

    <template v-slot:maincontent>
      <div class="modal-panel">
        <div class="modal-panel--left" :class="{ initiated: (loading || (!loading && logoRequestObj.logo_url)) }">
          <div class="titlewrap">
            <TitleBar variant-class="title-modal title-modal--simple">
              <template v-slot:pretitle>
                {{ $gettext('AI powered') }}
              </template>
              <template v-slot:title>
                {{ $gettext('Design your logo') }}
              </template>
            </TitleBar>
          </div>
          <div class="resultswrap">
            <transition name="pop" appear mode="out-in">
              
              <div v-if="!loading && !logoRequestObj.logo_url" style="display: flex; flex-direction: column; height: 100%; align-items: center; justify-content: center; position: absolute;">

                <AnimationItem 
                    class="anim-idle" 
                    :animation="{ animationData: idleAnimation.default }" 
                    :key="'anim_idle'" />

              </div>

            </transition>
            <transition name="pop" appear mode="out-in">
              <div v-if="loading" class="loading-img-wrap">
                <img :src="loadingGif" class="loading-img">
              </div>
            </transition>
            <transition name="pop" appear mode="out-in">
              <div v-if="!loading && logoRequestObj.logo_url && 0 < logoRequestHistory.length <= 1" class="logo-img-wrap">
                <img v-if="logoRequestHistory.length === 1" :src="logoRequestObj.logo_url" :alt="$gettext('AI Generated Logo')" class="logo-img">
                <b-carousel
                  v-if="logoRequestHistory.length > 1"
                  :interval="0"
                  controls
                  class="img-carousel">
                  <b-carousel-slide v-for="img in logoRequestHistory" :key="img.id" :img-src="img.logo_url" class="logo-img" />
                </b-carousel>
              </div>
            </transition>
          </div>
        </div>
        <div class="modal-panel--right gk-ai-assistant">
          <div class="conversation">
            <overlay-scrollbars ref="ai-conversation" :options="{ scrollbars: { autoHide: 'leave' }, overflowBehavior: { y: 'scroll', x: 'scroll' } }">
              <div class="content">
                <div v-for="(message, index) in conversation" :key="index" class="message" :class="['message--' + message.author]">
                  <div class="message--avatar">
                    <div v-if="(message.author === 'ai')"><font-awesome-icon icon="fa-solid fa-message-bot" /></div>
                    <Avatar v-if="(message.author === 'user')" :userId="currentUser.id" />
                  </div>
                  <div class="message--text">
                    <span class="message-tail">
                      <svg v-if="(message.author === 'ai')" viewBox="0 0 8 13" height="13" width="8" preserveAspectRatio="xMidYMid meet" class="" version="1.1" x="0px" y="0px" enable-background="new 0 0 8 13" xml:space="preserve"><path opacity="0.13" fill="#0000000" d="M1.533,3.568L8,12.193V1H2.812 C1.042,1,0.474,2.156,1.533,3.568z"></path><path fill="currentColor" d="M1.533,2.568L8,11.193V0L2.812,0C1.042,0,0.474,1.156,1.533,2.568z"></path></svg>
                      <svg v-if="(message.author === 'user')" viewBox="0 0 8 13" height="13" width="8" preserveAspectRatio="xMidYMid meet" class="" version="1.1" x="0px" y="0px" enable-background="new 0 0 8 13" xml:space="preserve"><path opacity="0.13" d="M5.188,1H0v11.193l6.467-8.625 C7.526,2.156,6.958,1,5.188,1z"></path><path fill="currentColor" d="M5.188,0H0v11.193l6.467-8.625C7.526,1.156,6.958,0,5.188,0z"></path></svg>
                    </span>

                    <div v-if="(message.author === 'ai' && message.message === 'intro')" class="message--text__content">
                      <div>{{ $gettext('THIS IS A BETA TESTING PRODUCT. This product will not yet yield the results that Gekko expects from it. You are however free to try this and you are also free to comment on this product by sending your feedback to support@getgekko.com. When you try to use this generator, there are some tips and tricks:') }}</div>
                      <br>
                      <div>{{ $gettext('Use simple commands like: "cat logo" or "hammer and man logo".') }}</div>
                      <br>
                      <div>{{ $gettext('There are only limited functions for now, so please understand that some colorings and / or styles are not yet available. Do not try to update the styles via your description as the logo generator will not work. If you want a different style, please contact support@getgekko.com to ask for an update. The logo\'s are not transparant yet. Therefore you should use make them transparant for better results (for instance via https://www.remove.bg/)') }}</div>
                      <br>
                      <div>{{ $gettext('Consider where the logo will appear (website, business cards, billboards). Test how it looks at different sizes.') }}</div>
                      <br>
                      <div>{{ $gettext('Be aware that your generated logo is not checked for trademark infringement. Be aware that other companies could force you to change your logo in case it is too similar to theirs.') }}</div>
                    </div>

                    <div v-else-if="(message.author === 'ai' && message.message === 'processing')" class="message--text__content">
                      {{ $gettext('Hold on for a second... We are creating the logo for you!') }}
                      <br>
                      <svg id="dots" width="32px" height="32px" viewBox="0 0 132 58" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                          <g id="dots" fill="#3333337d">
                            <circle id="dot1" cx="25" cy="30" r="13"></circle>
                            <circle id="dot2" cx="65" cy="30" r="13"></circle>
                            <circle id="dot3" cx="105" cy="30" r="13"></circle>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <div v-else-if="(message.author === 'ai' && logoRequestObj.revised_prompt)" class="message--text__content">
                      <div v-html="message.message" />
                      <GekkoButton
                        v-if="index === (conversation.length - 1)"
                        :text="$gettext('Set as company logo')"
                        @click="setAsCompanyLogo()"
                        class="m-2"
                      />
                    </div>

                    <div v-else-if="message.message" class="message--text__content" v-html="message.message" />

                    <div v-else class="message--text__content">
                      <svg id="dots" width="32px" height="32px" viewBox="0 0 132 58" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                          <g id="dots" fill="#3333337d">
                            <circle id="dot1" cx="25" cy="30" r="13"></circle>
                            <circle id="dot2" cx="65" cy="30" r="13"></circle>
                            <circle id="dot3" cx="105" cy="30" r="13"></circle>
                          </g>
                        </g>
                      </svg>
                    </div>
                  </div>
                </div>

              </div>
            </overlay-scrollbars>
          </div>

          <div class="prompt-wrap">
            <div class="d-flex">
              <b-form-group label-class="gk-label-margin gk-modal-label">
                <b-input-group>
                  <b-form-select size="sm" v-model="logoRequestObj.design_style" :options="designStyleOptions"></b-form-select>
                </b-input-group>
              </b-form-group>
            </div>
            <div class="d-flex">
              <b-form-group label-class="gk-label-margin gk-modal-label">
                <b-input-group>
                  <b-form-select size="sm" v-model="logoRequestObj.color_palette" :options="colorPaletteOptions"></b-form-select>
                </b-input-group>
              </b-form-group>
            </div>
            <div class="prompt">
              <b-form-textarea
                v-model="logoRequestObj.message"
                @keydown="sendPromptOnEnter"
                :placeholder="$gettext('Design a logo for an accounting company, showing a green smiling and friendly lizard.')"
                rows="4"
                no-resize
              />
              <GekkoButton 
                @click="sendMessage"
                :disabled="(loading || !logoRequestObj.message || logoRequestObj.message.length < 0)"
                :text="$gettext('Send')"
                :icon="'fa-solid fa-paper-plane-top'"
                class="send-message-btn"
              />
              <div class="prompt__text-count" :class="{ maxfill: logoRequestObj.message.length >= maxPromptLength }">
                {{ logoRequestObj.message.length }}/{{ maxPromptLength }}
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters } from 'vuex'

import * as idleAnimation from '@/css/animations/idle.json'

import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import Avatar from "@/components/Avatar";

import AiAssistantService from '@/store/services/ai.assistant.service.js'
import CompanyService from '@/store/services/company.service.js'

export default {
  name: 'LogoGenerator',
  components: { 
    ModalBase,
    TitleBar,
    GekkoButton,
    AnimationItem,
    Avatar
  },
  data () {
    return {
      logoBotId: null,
      loading: false,
      designStyleOptions: [],
      colorPaletteOptions: [],
      defaultRequestObj: {
        message: ''
      },
      logoRequestObj: null,
      logoRequestHistory: [],
      loadingModal: false,
      maxPromptLength: null,
      idleAnimation: idleAnimation,
      conversation: []
    }
  },
  computed: {
    ...mapGetters ({
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser'
    }),
    loadingGif () {
      let ranInt = Math.floor(Math.random() * 6)
      let loading_gif = 'dali'
      switch (ranInt) {
        case 0:
          loading_gif = 'dali'
          break;
        case 1:
          loading_gif = 'magritte'
          break;
        case 2:
          loading_gif = 'monalisa'
          break;
        case 3:
          loading_gif = 'picasso'
          break;
        case 4:
          loading_gif = 'van_gogh'
          break;
        case 5:
          loading_gif = 'vermeer'
          break;
      }
      return '/static/public/img/loading-gifs/' + loading_gif + '.gif'
    }
  },
  watch: {
    
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    initializeConversation () {
      if (!this.conversation.map(c => c.message).includes('intro')) {
        this.conversation.push({ author: 'ai', message: null });
        setTimeout(() => {
          this.fillResponseMessage('intro')
        }, 1000);
      }
    },
    setupData () {
      this.logoRequestObj = this.defaultRequestObj

      AiAssistantService.getCurrentLogoBot().then((response) => {
        this.maxPromptLength = response.data.max_prompt_length
        this.logoBotId = response.data.id

        this.initializeConversation()
        this.scrollConversation()

        AiAssistantService.getLogoStyleOptions().then((response) => {
          this.designStyleOptions = response.data.design_styles.filter(x => x.type === 'style').map(option => {
            return {
              value: option.id,
              text: option.name
            }
          })

          this.colorPaletteOptions = response.data.color_palettes.filter(x => x.type === 'palette').map(option => {
            return {
              value: option.id,
              text: option.name
            }
          })

          this.logoRequestObj.design_style = this.designStyleOptions[0].value
          this.logoRequestObj.color_palette = this.colorPaletteOptions[0].value
        })
      })
    },
    sendMessage () {

      this.conversation.push({
        author: 'user',
        message: this.logoRequestObj.message
      })
      this.scrollConversation()

      this.conversation.push({ author: 'ai', message: 'processing' });

      this.scrollConversation()

      this.loading = true
      this.logoRequestObj.bot_id = this.logoBotId

      const logoRequestCopy = Object.assign({}, this.logoRequestObj)
        
      this.logoRequestObj.message = '' // reset textarea


      AiAssistantService.sendLogoRequest(logoRequestCopy).then((response) => {
        if (response.data) {
          response.data.message = ''
          this.logoRequestObj = response.data
          this.logoRequestHistory.unshift(this.logoRequestObj)
        } else {
          this.logoRequestObj = this.defaultRequestObj
        }
        
        let ai_response_msg
        if (this.logoRequestObj.revised_prompt) {
          ai_response_msg = 'Revised prompt:<br>' + this.logoRequestObj.revised_prompt
        } else {
          ai_response_msg = this.$gettext('we could not process your request, please try again in a moment')
        }

        this.fillResponseMessage(ai_response_msg)

        this.loading = false
      }).catch((error) => {
        let premsg = this.$gettext('Failed to process your message: ')
        let msg = this.$gettext('GekkoBot not responding.')
        if (error.status === 403) {
          premsg = this.$gettext('You\'ve reached your limit of free consultations: ')
          msg = this.$gettext('Please consider upgrading to Premium to keep using GekkoBot.')
          this.$store.commit('togglePremiumModal', 'web_ai_assistant')
          window.mixpanel.track("Gekkobot: error - limit of free consultations")
        } else {
          if (error.status === 405) {
            msg = this.$gettext('You are not allowed to use the chat bot feature.')
            window.mixpanel.track("Gekkobot: error - not allowed")
          } else if (error.status === 429) {
            msg = this.$gettext('You have already sent too many messages.')
            window.mixpanel.track("Gekkobot: error - too many messages")
          }
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: msg,
            variant: "danger",
            duration: 4
          })
        }
        this.loading = false
      })
    },
    async setAsCompanyLogo () {
      this.loadingModal = true

      const response = await fetch(this.logoRequestObj.logo_url)
      const blob = await response.blob()
      const file = new File([blob], 'logo_image.jpg', { type: blob.type })
      
      let companyData = {
        id: this.currentCompany.id,
        name: this.currentCompany.name,
        country: this.currentCompany.country,
        logo: file
      }

      CompanyService.updateLogo(this.currentCompany.id, companyData).then((response) => {
        this.logoRequestObj = {
          message: '',
          style: this.designStyleOptions[0].value
        }
        this.loadingModal = false
        this.$emit('logoSetToCompany', response.data)
        this.close()
      }).catch(() => {
        this.$store.commit('setAlert', {
          title: this.$gettext('Error'),
          text: this.$gettext('We could not update your company logo'),
          variant: "danger",
          duration: 4
        })
      })
    },
    scrollConversation () {
    // conversation scroll bottom
      this.$nextTick(() => {
        const osInstance = this.$refs['ai-conversation'].osInstance();
        osInstance.scroll({
          y: '100%'
        }, 300);
      })
    },
    sendPromptOnEnter (e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (this.logoRequestObj.message && !!this.logoRequestObj.message.trim()) {
          this.sendMessage()
        } else {
          this.logoRequestObj.message = null
        }
      }
    },
    fillResponseMessage (response) {
      const response_item = this.conversation[this.conversation.length - 1]
      
      if (response_item) {
        if (response_item.author === 'ai' && (!response_item.message || response_item.message === 'processing')) {
          if (response === 'intro') {
            response_item.message = response;
          } else {
            response_item.message = this.parseGPTResponse(response);
          }
          this.scrollConversation();
        }
      } else {
        response_item.message = this.parseGPTResponse(this.$gettext("Something went wrong, please try refreshing the page."));
        this.scrollConversation();
      }
    },
    parseGPTResponse (message) {
      let parsed_message = message

      // Replace new lines with <br> tags
      parsed_message = parsed_message.replace(/\n/g, '<br>')

      // Split the generated text into paragraphs
      const paragraphs = parsed_message.split('<br><br>');

      // Wrap each paragraph in <p> tags
      parsed_message = paragraphs.map(paragraph => `<p>${paragraph}</p>`).join('');

      return parsed_message
    },
  }
}
</script>

<style lang="scss">
.logo-generator-modal {
  .gk-modal-wrapper {
    .gk-modal {
    }
  }
  .gk-ai-assistant .conversation .content .message {
    padding-top: 1rem;
  }

  .carousel-control-next-icon, .carousel-control-prev-icon {
    background-color: $colorBrand;
    padding: 1rem;
    border-radius: 50%;
    border: solid $colorBrand 0.5rem;
  }

  .img-carousel {
    width: 100%;
    object-fit: contain;
    filter: drop-shadow(2px 2px 10px rgba(0, 0, 0, 0.5));
  }
}
</style>

<style lang="scss" scoped>
.logo-generator-modal {
  .modal-panel {
    display: flex;
    width: 100%;
    height: 100%;

    flex-direction: column;

    &--left, &--right {
      width: 100%;
      height: 50%;
    }
    
    @media (min-width: '768px') {
      flex-direction: row;
      &--left {
        width: 55%;
        height: 100%;
      }
      &--right {
        width: 45%;
        height: 100%;
      }
    }

    &--left {
      background: white;
      transition: background-color 0.7s ease;
      .titlewrap {
        .titlebar.title-modal {
          margin-bottom: 0;
          .action-bar { display: none; }
        }
      }
      .resultswrap {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        height: calc(100% - 83px);
        position: relative;
        background: white;
        transition: background-color 0.7s ease;
      }
      &.initiated {
        .resultswrap {
          background: #dddddd;
        }
      }
    }
    &--right {
      font-size: 1rem;
      display: flex;
      flex-direction: column;
      background: $chatbotPanelBg;
      .instructions-wrap {
        flex: 1;
      }
      .prompt-wrap {
        padding: 1rem;
        padding-top: 0.5rem;
        border-top: 1px solid $colorBorderDash;
        .form-group {
          margin-bottom: 0.5rem;
        }
        .custom-select {
          background-color: white;
        }
      }
    }

  }
}



.loading-img-wrap {
  border-radius: $radiusLg;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  max-height: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  .loading-img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
}


.logo-img-wrap {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  .logo-img {
    height: 100%;
    width: 100%;
    object-fit: contain;
    filter: drop-shadow(2px 2px 10px rgba(0, 0, 0, 0.5));
  }
}

.anim-idle {
  width: 100%;
  height: 100%;
  margin: 0 auto;
}

</style>